import React from 'react'
import { Paperclip } from 'lucide-react'

export interface Segment {
  duration: number
  color: string
  topLabel?: string
  label?: string
  subLabel?: string
  badge?: string
  onMouseDown?: (e: React.MouseEvent) => void
  cursor?: string
  hasAttachments?: boolean
  pulsing?: boolean
  statusPill?: string
}

interface Props {
  segments: Segment[]
  left: number
  width: number
  className?: string
  style?: React.CSSProperties
  onMouseDown?: (e: React.MouseEvent) => void
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void
  children?: React.ReactNode;
  laneIndex?: number
  laneCount?: number
}

const SegmentCell = ({ duration, color, topLabel, label, subLabel, badge, onMouseDown, cursor, hasAttachments, pulsing, statusPill }: Segment) => {
  if (duration === 0) return null

  const hasBottom = !!(subLabel || badge)

  return (
    <div
      className="relative h-full overflow-hidden flex flex-col py-1 px-1.5 text-white font-medium leading-tight rounded-sm"
      style={{ flex: duration, backgroundColor: color, minWidth: 4, cursor, justifyContent: hasBottom ? 'space-between' : 'center' }}
      onMouseDown={onMouseDown}
    >
      <div className="flex flex-col gap-px overflow-hidden">
        {(statusPill || topLabel) && (
          <div className="flex items-center gap-1 overflow-hidden">
            {statusPill && (
              <span className="inline-flex items-center gap-1 px-1 py-px rounded bg-red-500 text-white text-[8px] font-bold uppercase leading-none shrink-0">
                <span className="w-1 h-1 rounded-full bg-white animate-pulse shrink-0" />
                <span>{statusPill}</span>
              </span>
            )}
            {topLabel && <span className="text-[11px] font-semibold truncate">{topLabel}</span>}
          </div>
        )}
        {label && <span className={`text-xs truncate ${!topLabel ? 'leading-snug' : ''}`}>{label}</span>}
      </div>
      {hasBottom && (
        <div className="flex flex-col gap-px overflow-hidden">
          {subLabel && <span className="text-[10px] truncate opacity-80">{subLabel}</span>}
          {badge && <span className="text-[9px] leading-none opacity-75 tabular-nums truncate">{badge}</span>}
        </div>
      )}
      {hasAttachments && (
        <span className="absolute top-1 right-1 z-10 pointer-events-none">
          <Paperclip className="w-2.5 h-2.5 opacity-90" />
        </span>
      )}
      {pulsing && (
        <div
          className="absolute inset-0 rounded-sm ring-2 ring-inset ring-white animate-pulse pointer-events-none"
          aria-hidden
        />
      )}
    </div>
  )
}

export const SegmentedBlock: React.FC<Props> = ({
  segments,
  left,
  width,
  className = '',
  style,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
  children,
  laneIndex,
  laneCount,
}) => {
  const useLanes = laneCount != null && laneCount > 1 && laneIndex != null
  const laneStyle: React.CSSProperties = useLanes
    ? {
        top: `calc(${(laneIndex! / laneCount!) * 100}% + 2px)`,
        height: `calc(${(1 / laneCount!) * 100}% - 4px)`,
      }
    : { top: 4, bottom: 4 }

  return (
    <div
      className={`absolute flex rounded overflow-hidden gap-px ${className}`}
      style={{ left: `${left}%`, width: `${width}%`, ...laneStyle, ...style }}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {segments.map((seg, i) => (
        <SegmentCell key={i} {...seg} />
      ))}
      {children}
    </div>
  )
}
